import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { getExtendedMetadata } from '@/types/treatment-record-request-extended';

import { DEFAULT_VOICE_AI_INTERVAL } from '../../../../constants';

/**
 * Custom hook to manage Voice AI interval updates
 * @param request - The treatment record request
 * @param matterId - The matter ID
 * @param providerId - The provider ID
 * @param requestId - The request ID
 * @returns Voice AI interval update state and handlers
 */
export function useVoiceAIIntervalUpdate(
  request: TreatmentRecordRequestSchema,
  matterId: string,
  providerId: string,
  requestId: string,
) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedInterval, setEditedInterval] = useState(
    DEFAULT_VOICE_AI_INTERVAL,
  );
  const [updateRequest] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsUpdateMutation();

  // Initialize edited interval from request metadata
  useEffect(() => {
    const extendedMetadata = getExtendedMetadata(request.metadata);
    setEditedInterval(
      extendedMetadata.voice_ai_check_interval_days ||
        DEFAULT_VOICE_AI_INTERVAL,
    );
  }, [request.metadata]);

  const handleUpdate = async () => {
    try {
      await updateRequest({
        matterId,
        providerId,
        requestId,
        updateTreatmentRecordRequestSchema: {
          request_id: requestId,
          metadata: {
            ...request.metadata,
            voice_ai_check_interval_days: editedInterval,
          } as any, // Extended metadata fields are handled by backend
        },
      }).unwrap();
      toast.success('Updated follow-up frequency');
      setIsEditing(false);
      return true;
    } catch (error) {
      toast.error('Failed to update follow-up frequency');
      return false;
    }
  };

  const handleCancel = () => {
    const extendedMetadata = getExtendedMetadata(request.metadata);
    setIsEditing(false);
    setEditedInterval(
      extendedMetadata.voice_ai_check_interval_days ||
        DEFAULT_VOICE_AI_INTERVAL,
    );
  };

  return {
    isEditing,
    setIsEditing,
    editedInterval,
    setEditedInterval,
    handleUpdate,
    handleCancel,
  };
}
